import React, { useState } from 'react';
import axios from 'axios';
import { CalendarX, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

/**
 * QuickBlockPanel
 * ===============
 * Studio-wide block for off-site bookings (customer booked via text/call/DM).
 * NO engineer picker — blocks ALL engineers at that time because the studio
 * is a shared physical space. For engineer-specific time off (sick day, PTO)
 * use the TimeOffPanel below.
 */
const QuickBlockPanel = () => {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [startTime, setStartTime] = useState('9:00 PM');
  const [hours, setHours] = useState(3);
  const [customerName, setCustomerName] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const times = [
    '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM',
    '4:00 PM', '5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM', '9:00 PM',
    '10:00 PM', '11:00 PM', '12:00 AM', '1:00 AM', '2:00 AM', '3:00 AM', '4:00 AM',
  ];

  const submit = async () => {
    if (!customerName.trim()) {
      toast.error('Enter the customer name so you know who booked it');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/admin/quick-block`, {
        date,
        start_time: startTime,
        hours: parseInt(hours, 10),
        customer_name: customerName.trim(),
        note: note.trim() || `Off-site booking · ${customerName}`,
      });
      toast.success(`✅ Studio blocked · ${r.data.slots_total} slots · ${r.data.booking_number}`);
      setCustomerName('');
      setNote('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Block failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="quick-block-panel"
      className="rounded-2xl border border-orange-500/30 bg-gradient-to-br from-orange-500/5 to-red-500/5 p-5"
    >
      <div className="flex items-center gap-2 mb-4">
        <div className="p-2 rounded-lg bg-orange-500/20 border border-orange-400/40">
          <CalendarX className="w-5 h-5 text-orange-300" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-white">Block Studio (Off-site Booking)</h3>
          <p className="text-xs text-orange-200/70">
            Customer booked off-site (text/call/DM)? Blocks ALL engineers at that time —
            the studio is a shared space. For engineer-specific time off, use the section below.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-orange-300 mb-1">Customer Name</label>
          <input
            data-testid="qb-customer-name"
            type="text"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            placeholder="Who booked? (e.g., Moze)"
            className="w-full px-3 py-2 rounded-lg bg-black/50 border border-orange-500/40 text-white text-sm focus:outline-none focus:border-orange-400"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-orange-300 mb-1">Date</label>
          <input
            data-testid="qb-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-black/50 border border-orange-500/40 text-white text-sm focus:outline-none focus:border-orange-400"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-orange-300 mb-1">Start Time</label>
          <select
            data-testid="qb-start-time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-black/50 border border-orange-500/40 text-white text-sm focus:outline-none focus:border-orange-400"
          >
            {times.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-orange-300 mb-1">Hours</label>
          <select
            data-testid="qb-hours"
            value={hours}
            onChange={(e) => setHours(parseInt(e.target.value, 10))}
            className="w-full px-3 py-2 rounded-lg bg-black/50 border border-orange-500/40 text-white text-sm focus:outline-none focus:border-orange-400"
          >
            {[1, 2, 3, 4, 5, 6, 7, 8].map((h) => (
              <option key={h} value={h}>{h} hr{h > 1 ? 's' : ''}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-3">
        <label className="block text-xs font-semibold text-orange-300 mb-1">Note (optional)</label>
        <input
          data-testid="qb-note"
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g., Booked via Instagram DM"
          className="w-full px-3 py-2 rounded-lg bg-black/50 border border-orange-500/40 text-white text-sm focus:outline-none focus:border-orange-400"
        />
      </div>

      <button
        data-testid="qb-submit"
        onClick={submit}
        disabled={submitting}
        className="w-full mt-4 py-2.5 rounded-lg bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-400 hover:to-red-400 text-white font-bold text-sm disabled:opacity-50 transition-all flex items-center justify-center gap-2"
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Blocking…
          </>
        ) : (
          <>Block Studio</>
        )}
      </button>

      <p className="text-[10px] text-orange-200/50 text-center mt-2">
        Blocks the studio for ALL engineers at this time. A mirror row is added to the calendar with the customer&apos;s name.
      </p>
    </div>
  );
};

export default QuickBlockPanel;
